/**
 * Admin Service - Direct Database Version
 * Handles admin operations via direct Supabase connections
 */

import { firebaseAuthService } from '@/shared/services/auth/firebaseAuthService';
import { supabaseService } from '@/shared/services/supabaseService';
import { AuditLog } from '@/shared/types/system';

export interface AdminStats {
  totalUsers: number;
  totalRevenue: number;
  totalInvoices: number;
  totalQuotes: number;
  recentTransactions: Array<{
    id: string;
    invoice_number: string;
    total_amount: number;
    status: string;
    created_at: string;
    client_name: string;
  }>;
}

export interface UserGrowthData {
  period: string;
  users: number;
  change?: string;
}

export interface RevenueByTier {
  tier: string;
  amount: number;
  percentage: number;
  users: number;
}

export interface MonthlyRevenue {
  month: string;
  revenue: number;
  transactions: number;
}

export interface SystemTransaction {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  amount: number;
  currency: string;
  status: 'succeeded' | 'pending' | 'failed';
  planId: string;
  date: string;
  paymentMethod?: string;
}

export interface OutstandingInvoice {
  id: string;
  invoiceNumber: string;
  clientName: string;
  amount: number;
  dueDate: string;
  daysOverdue: number;
  userId: string;
}

export interface RecentPayment {
  id: string;
  clientName: string;
  amount: number;
  date: string;
  method: string;
  status: 'succeeded' | 'pending' | 'failed';
  invoiceId?: string;
}

export interface AdminUser {
  id: string;
  email: string;
  display_name: string;
  subscription_plan: string;
  role: string;
  created_at: string;
  last_sign_in?: string;
}

export const adminServiceAPI = {
  /**
   * Get admin dashboard statistics
   */
  async getAdminStats(): Promise<AdminStats> {
    const currentUser = await firebaseAuthService.getCurrentUser();

    if (!currentUser) {
      throw new Error('Authentication required');
    }

    // Check if current user is admin from database
    const { data: currentUserProfile, error: profileError } = await supabaseService.client
      .from('profiles')
      .select('role')
      .eq('email', currentUser.email)
      .single();

    if (profileError || !currentUserProfile || currentUserProfile.role !== 'admin') {
      throw new Error('Admin access required');
    }

    try {
      // Get total users count
      const { count: totalUsers } = await supabaseService.client
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      // Get total revenue from invoices
      const { data: invoices } = await supabaseService.client
        .from('invoices')
        .select('total_amount')
        .eq('status', 'paid');

      const invoiceRevenue = invoices?.reduce((sum: number, inv: any) => sum + (parseFloat(inv.total_amount) || 0), 0) || 0;

      // Get revenue from active PayPal subscriptions
      const { data: activeSubscriptions } = await supabaseService.client
        .from('paypal_billing_agreements')
        .select(`
          status,
          profiles:user_id (
            subscription_plan_id
          )
        `)
        .eq('status', 'active');

      const subscriptionRevenue = activeSubscriptions?.reduce((sum: number, sub: any) => {
        const planId: 'free' | 'basic' | 'pro' | 'enterprise' = (sub.profiles?.subscription_plan_id || 'free') as 'free' | 'basic' | 'pro' | 'enterprise';
        const planAmount = {
          'free': 0,
          'basic': 1,
          'pro': 10,
          'enterprise': 29
        }[planId] || 0;
        return sum + planAmount;
      }, 0) || 0;

      const totalRevenue = invoiceRevenue + subscriptionRevenue;

      // Get total invoices count
      const { count: totalInvoices } = await supabaseService.client
        .from('invoices')
        .select('*', { count: 'exact', head: true });

      // Get total quotes count
      const { count: totalQuotes } = await supabaseService.client
        .from('quotes')
        .select('*', { count: 'exact', head: true });

      return {
        totalUsers: totalUsers || 0,
        totalRevenue: totalRevenue,
        totalInvoices: totalInvoices || 0,
        totalQuotes: totalQuotes || 0,
        recentTransactions: []
      };
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      throw new Error('Failed to fetch admin statistics');
    }
  },

  /**
   * Get all users
   */
  async getAllUsers(): Promise<AdminUser[]> {
    const currentUser = await firebaseAuthService.getCurrentUser();

    if (!currentUser) {
      throw new Error('Authentication required');
    }

    // First check if the current user is an admin by looking up their role in the database
    const { data: currentUserProfile, error: profileError } = await supabaseService.client
      .from('profiles')
      .select('role')
      .eq('email', currentUser.email)
      .single();

    if (profileError || !currentUserProfile || currentUserProfile.role !== 'admin') {
      throw new Error('Admin access required');
    }

    // Now fetch all users
    const { data, error } = await supabaseService.client
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching users:', error);
      throw new Error('Failed to fetch users');
    }

    return (data || []).map((user: any) => ({
      id: user.id,
      email: user.email,
      display_name: user.full_name || user.name || 'Unknown',
      subscription_plan: user.subscription_plan_id || 'free',
      role: user.role || 'freelancer',
      created_at: user.created_at,
      last_sign_in: user.last_sign_in_at
    }));
  },

  /**
   * Update user's subscription plan
   */
  async updateUserPlan(userId: string, planId: string, billingPeriod: string = 'monthly'): Promise<void> {
    const currentUser = await firebaseAuthService.getCurrentUser();

    if (!currentUser) {
      throw new Error('Authentication required');
    }

    // Check if current user is admin from database
    const { data: currentUserProfile, error: profileError } = await supabaseService.client
      .from('profiles')
      .select('role')
      .eq('email', currentUser.email)
      .single();

    if (profileError || !currentUserProfile || currentUserProfile.role !== 'admin') {
      throw new Error('Admin access required');
    }

    // Calculate next billing date based on billing period
    let nextBillingDate: string | undefined;

    if (planId !== 'free') {
      const nextDate = new Date();

      switch (billingPeriod) {
        case 'monthly':
          nextDate.setMonth(nextDate.getMonth() + 1);
          break;
        case 'quarterly':
          nextDate.setMonth(nextDate.getMonth() + 3);
          break;
        case 'yearly':
          nextDate.setFullYear(nextDate.getFullYear() + 1);
          break;
        default:
          nextDate.setMonth(nextDate.getMonth() + 1);
      }

      nextBillingDate = nextDate.toISOString();
    }

    const { error } = await supabaseService.client
      .from('profiles')
      .update({
        subscription_plan_id: planId,
        subscription_status: 'active',
        next_billing_date: nextBillingDate,
        billing_period: billingPeriod,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);

    if (error) {
      throw new Error('Failed to update user plan');
    }
  },

  /**
   * Delete a user
   */
  async deleteUser(userId: string): Promise<void> {
    const currentUser = await firebaseAuthService.getCurrentUser();

    if (!currentUser) {
      throw new Error('Authentication required');
    }

    // Check if current user is admin from database
    const { data: currentUserProfile, error: profileError } = await supabaseService.client
      .from('profiles')
      .select('role')
      .eq('email', currentUser.email)
      .single();

    if (profileError || !currentUserProfile || currentUserProfile.role !== 'admin') {
      throw new Error('Admin access required');
    }

    // First check if user has associated data
    const { data: invoices } = await supabaseService.client
      .from('invoices')
      .select('id')
      .eq('user_id', userId)
      .limit(1);

    const { data: quotes } = await supabaseService.client
      .from('quotes')
      .select('id')
      .eq('user_id', userId)
      .limit(1);

    if ((invoices && invoices.length > 0) || (quotes && quotes.length > 0)) {
      throw new Error('Cannot delete user: User has associated invoices or quotes');
    }

    // Delete the user
    const { error } = await supabaseService.client
      .from('profiles')
      .delete()
      .eq('id', userId);

    if (error) {
      throw new Error('Failed to delete user');
    }
  },

  /**
   * Get user growth data by week for the last 4 weeks
   */
  async getUserGrowth(): Promise<UserGrowthData[]> {
    const { data, error } = await supabaseService.client
      .from('profiles')
      .select('created_at')
      .gte('created_at', new Date(Date.now() - 28 * 24 * 60 * 60 * 1000).toISOString())
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching user growth:', error);
      return [];
    }

    // Group by week
    const weeklyData: { [key: string]: number } = {};
    const now = new Date();

    // Initialize last 4 weeks
    for (let i = 3; i >= 0; i--) {
      const weekDate = new Date(now);
      weekDate.setDate(now.getDate() - (i * 7));
      const weekKey = `Week ${4 - i}`;
      weeklyData[weekKey] = 0;
    }

    // Count users per week
    data?.forEach((user: any) => {
      const userDate = new Date(user.created_at);
      const weeksDiff = Math.floor((now.getTime() - userDate.getTime()) / (7 * 24 * 60 * 60 * 1000));
      if (weeksDiff < 4) {
        const weekKey = `Week ${4 - weeksDiff}`;
        weeklyData[weekKey] = (weeklyData[weekKey] || 0) + 1;
      }
    });

    return Object.entries(weeklyData).map(([period, users], index) => {
      const prevUsers = index > 0 ? (Object.values(weeklyData)[index - 1] as number) : 0;
      const change = prevUsers > 0 ? (((users - prevUsers) / prevUsers) * 100).toFixed(0) : users > 0 ? '100' : '0';
      return {
        period,
        users,
        change: change.startsWith('-') ? change : `+${change}%`
      };
    });
  },

  /**
   * Get revenue distribution by subscription tier
   */
  async getRevenueByTier(): Promise<RevenueByTier[]> {
    const { data, error } = await supabaseService.client
      .from('paypal_billing_agreements')
      .select(`
        amount,
        status,
        profiles:user_id (
          subscription_plan_id
        )
      `)
      .eq('status', 'active');

    if (error) {
      console.error('Error fetching revenue by tier:', error);
      return [];
    }

    // Group by subscription plan
    const tierRevenue: Record<string, { amount: number; users: Set<string> }> = {
      'Enterprise': { amount: 0, users: new Set() },
      'Professional': { amount: 0, users: new Set() },
      'Starter': { amount: 0, users: new Set() }
    };

    let totalRevenue = 0;

    data?.forEach((item: any) => {
      const planId = item.profiles?.subscription_plan_id || 'free';
      const amount = parseFloat(item.amount) || 0;
      const userId = item.profiles?.id || '';

      // Map plan IDs to display names
      let tier = 'Starter';
      if (planId === 'enterprise' || planId === 'pro') {
        tier = planId === 'enterprise' ? 'Enterprise' : 'Professional';
      }

      if (tierRevenue[tier as keyof typeof tierRevenue]) {
        tierRevenue[tier]!.amount += amount;
        if (userId) tierRevenue[tier]!.users.add(userId);
        totalRevenue += amount;
      }
    });

    return Object.entries(tierRevenue).map(([tier, data]) => ({
      tier,
      amount: data.amount,
      percentage: totalRevenue > 0 ? Math.round((data.amount / totalRevenue) * 100) : 0,
      users: data.users.size
    }));
  },

  /**
   * Get monthly revenue for the last 6 months
   */
  async getMonthlyRevenue(): Promise<MonthlyRevenue[]> {
    const { data, error } = await supabaseService.client
      .from('paypal_billing_agreements')
      .select(`
        created_at,
        amount,
        status,
        profiles:user_id (
          subscription_plan_id
        )
      `)
      .eq('status', 'active')
      .gte('created_at', new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString())
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching monthly revenue:', error);
      return [];
    }

    // Group by month
    const monthlyData: { [key: string]: { revenue: number; transactions: number } } = {};
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const now = new Date();

    // Initialize last 6 months
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthlyData[monthKey] = { revenue: 0, transactions: 0 };
    }

    // Aggregate revenue by month
    data?.forEach((item: any) => {
      const date = new Date(item.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      if (monthlyData[monthKey]) {
        const planId = item.profiles?.subscription_plan_id || 'free';
        const amount = parseFloat(item.amount) || 0;

        // Calculate revenue based on plan
        let planAmount = 0;
        switch (planId) {
          case 'enterprise':
            planAmount = 29;
            break;
          case 'pro':
            planAmount = 10;
            break;
          case 'basic':
            planAmount = 1;
            break;
          default:
            planAmount = 0;
        }

        monthlyData[monthKey].revenue += planAmount;
        monthlyData[monthKey].transactions += 1;
      }
    });

    // Convert to array format
    return Object.entries(monthlyData)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([monthKey, data]) => {
        const [year, month] = monthKey.split('-');
        const monthIndex = parseInt(month || '1') - 1;
        return {
          month: monthNames[monthIndex] || `Month ${monthIndex + 1}`,
          revenue: data.revenue,
          transactions: data.transactions
        };
      });
  },

  /**
   * Get all system transactions from subscriptions and payments
   */
  async getSystemTransactions(): Promise<SystemTransaction[]> {
    const currentUser = await firebaseAuthService.getCurrentUser();

    if (!currentUser) {
      throw new Error('Authentication required');
    }

    // Check if current user is admin
    const { data: currentUserProfile, error: profileError } = await supabaseService.client
      .from('profiles')
      .select('role')
      .eq('email', currentUser.email)
      .single();

    if (profileError || !currentUserProfile || currentUserProfile.role !== 'admin') {
      throw new Error('Admin access required');
    }

    try {
      const transactions: SystemTransaction[] = [];

      // Get transactions from PayPal billing agreements (active subscriptions)
      const { data: subscriptions } = await supabaseService.client
        .from('paypal_billing_agreements')
        .select(`
          id,
          amount,
          currency,
          status,
          created_at,
          activated_at,
          user_id,
          profiles:user_id (
            email,
            full_name,
            subscription_plan_id
          )
        `)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      const planAmounts: Record<string, number> = {
        'free': 0,
        'basic': 1,
        'pro': 10,
        'enterprise': 29
      };

      subscriptions?.forEach((sub: any) => {
        const planId = sub.profiles?.subscription_plan_id || 'free';
        const amount = parseFloat(sub.amount) || planAmounts[planId] || 0;

        transactions.push({
          id: sub.id,
          userId: sub.user_id,
          userName: sub.profiles?.full_name || 'Unknown',
          userEmail: sub.profiles?.email || 'unknown@example.com',
          amount,
          currency: sub.currency || 'USD',
          status: 'succeeded',
          planId,
          date: sub.activated_at || sub.created_at,
          paymentMethod: 'PayPal'
        });
      });

      // Get transactions from payments table
      const { data: payments } = await supabaseService.client
        .from('payments')
        .select(`
          id,
          amount,
          currency,
          status,
          payment_date,
          transaction_id,
          payment_method,
          user_id,
          profiles:user_id (
            email,
            full_name
          ),
          invoices:invoice_id (
            invoice_number
          )
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      payments?.forEach((payment: any) => {
        transactions.push({
          id: payment.id,
          userId: payment.user_id,
          userName: payment.profiles?.full_name || 'Unknown',
          userEmail: payment.profiles?.email || 'unknown@example.com',
          amount: parseFloat(payment.amount) || 0,
          currency: payment.currency || 'USD',
          status: payment.status as 'succeeded' | 'pending' | 'failed',
          planId: 'payment',
          date: payment.payment_date || payment.created_at,
          paymentMethod: payment.payment_method || 'Other'
        });
      });

      // Sort by date descending
      return transactions.sort((a, b) =>
        new Date(b.date).getTime() - new Date(a.date).getTime()
      );
    } catch (error) {
      console.error('Error fetching system transactions:', error);
      return [];
    }
  },

  /**
   * Get outstanding invoices (not paid, overdue)
   */
  async getOutstandingInvoices(): Promise<OutstandingInvoice[]> {
    const currentUser = await firebaseAuthService.getCurrentUser();

    if (!currentUser) {
      throw new Error('Authentication required');
    }

    // Check if current user is admin
    const { data: currentUserProfile, error: profileError } = await supabaseService.client
      .from('profiles')
      .select('role')
      .eq('email', currentUser.email)
      .single();

    if (profileError || !currentUserProfile || currentUserProfile.role !== 'admin') {
      throw new Error('Admin access required');
    }

    try {
      const today = new Date();

      const { data: invoices } = await supabaseService.client
        .from('invoices')
        .select(`
          id,
          invoice_number,
          total_amount,
          due_date,
          status,
          user_id,
          clients:client_id (
            name
          )
        `)
        .in('status', ['sent', 'overdue'])
        .order('due_date', { ascending: true });

      return (invoices || []).map((inv: any) => {
        const dueDate = new Date(inv.due_date);
        const daysOverdue = Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));

        return {
          id: inv.id,
          invoiceNumber: inv.invoice_number,
          clientName: inv.clients?.name || 'Unknown Client',
          amount: parseFloat(inv.total_amount) || 0,
          dueDate: inv.due_date,
          daysOverdue: Math.max(0, daysOverdue),
          userId: inv.user_id
        };
      });
    } catch (error) {
      console.error('Error fetching outstanding invoices:', error);
      return [];
    }
  },

  /**
   * Get recent payments from the payments table
   */
  async getRecentPayments(): Promise<RecentPayment[]> {
    const currentUser = await firebaseAuthService.getCurrentUser();

    if (!currentUser) {
      throw new Error('Authentication required');
    }

    // Check if current user is admin
    const { data: currentUserProfile, error: profileError } = await supabaseService.client
      .from('profiles')
      .select('role')
      .eq('email', currentUser.email)
      .single();

    if (profileError || !currentUserProfile || currentUserProfile.role !== 'admin') {
      throw new Error('Admin access required');
    }

    try {
      const { data: payments } = await supabaseService.client
        .from('payments')
        .select(`
          id,
          amount,
          currency,
          status,
          payment_date,
          payment_method,
          transaction_id,
          invoice_id,
          user_id,
          profiles:user_id (
            email,
            full_name
          ),
          invoices:invoice_id (
            invoice_number
          )
        `)
        .order('payment_date', { ascending: false })
        .limit(20);

      return (payments || []).map((payment: any) => ({
        id: payment.id,
        clientName: payment.profiles?.full_name || payment.profiles?.email || 'Unknown',
        amount: parseFloat(payment.amount) || 0,
        date: payment.payment_date || payment.created_at,
        method: payment.payment_method || 'Other',
        status: payment.status as 'succeeded' | 'pending' | 'failed',
        invoiceId: payment.invoice_id
      }));
    } catch (error) {
      console.error('Error fetching recent payments:', error);
      return [];
    }
  },

  /**
   * Get audit logs with optional filters
   */
  async getAuditLogs(filters?: {
    actionType?: string;
    severity?: string;
    startDate?: string;
    endDate?: string;
    userId?: string;
  }): Promise<AuditLog[]> {
    const currentUser = await firebaseAuthService.getCurrentUser();

    if (!currentUser) {
      throw new Error('Authentication required');
    }

    // Check if current user is admin
    const { data: currentUserProfile, error: profileError } = await supabaseService.client
      .from('profiles')
      .select('role')
      .eq('email', currentUser.email)
      .single();

    if (profileError || !currentUserProfile || currentUserProfile.role !== 'admin') {
      throw new Error('Admin access required');
    }

    try {
      let query = supabaseService.client
        .from('audit_logs')
        .select(`
          id,
          action,
          action_type,
          details,
          user_id,
          admin_user_id,
          severity,
          ip_address,
          user_agent,
          metadata,
          created_at,
          profiles:admin_user_id (
            full_name,
            email
          )
        `);

      // Apply filters
      if (filters?.actionType) {
        query = query.eq('action_type', filters.actionType);
      }

      if (filters?.severity) {
        query = query.eq('severity', filters.severity);
      }

      if (filters?.userId) {
        query = query.eq('user_id', filters.userId);
      }

      if (filters?.startDate) {
        query = query.gte('created_at', new Date(filters.startDate).toISOString());
      }

      if (filters?.endDate) {
        query = query.lte('created_at', new Date(filters.endDate).toISOString());
      }

      // Order by created_at descending and limit
      query = query.order('created_at', { ascending: false }).limit(500);

      const { data: logs, error } = await query;

      if (error) {
        console.error('Error fetching audit logs:', error);
        return [];
      }

      return (logs || []).map((log: any): AuditLog => ({
        id: log.id,
        action: log.action,
        actionType: log.action_type,
        details: log.details,
        userId: log.user_id,
        adminUserId: log.admin_user_id,
        adminUserName: log.profiles?.full_name || 'Unknown',
        adminUserEmail: log.profiles?.email || '',
        timestamp: log.created_at,
        severity: log.severity,
        ipAddress: log.ip_address,
        userAgent: log.user_agent
      }));
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      return [];
    }
  },

  /**
   * Create an audit log entry
   */
  async createAuditLog(data: {
    action: string;
    actionType?: 'user_management' | 'system_config' | 'authentication' | 'data_modification' | 'other';
    details: string;
    userId?: string;
    severity?: 'info' | 'warning' | 'critical';
    ipAddress?: string;
    userAgent?: string;
    metadata?: Record<string, unknown>;
  }): Promise<void> {
    const currentUser = await firebaseAuthService.getCurrentUser();

    if (!currentUser) {
      return;
    }

    try {
      await supabaseService.client
        .from('audit_logs')
        .insert({
          action: data.action,
          action_type: data.actionType,
          details: data.details,
          user_id: data.userId,
          admin_user_id: currentUser.id,
          severity: data.severity || 'info',
          ip_address: data.ipAddress,
          user_agent: data.userAgent,
          metadata: data.metadata || {}
        });
    } catch (error) {
      console.error('Error creating audit log:', error);
      // Don't throw - audit logging failures shouldn't break the main flow
    }
  },

  /**
   * Get recent activities from various tables
   */
  async getRecentActivities(limit: number = 10): Promise<Array<{
    id: string;
    type: 'user' | 'subscription' | 'payment' | 'invoice' | 'quote';
    action: string;
    description: string;
    timestamp: string;
    user?: string;
    userId?: string;
  }>> {
    const currentUser = await firebaseAuthService.getCurrentUser();

    if (!currentUser) {
      throw new Error('Authentication required');
    }

    // Check if current user is admin
    const { data: currentUserProfile, error: profileError } = await supabaseService.client
      .from('profiles')
      .select('role')
      .eq('email', currentUser.email)
      .single();

    if (profileError || !currentUserProfile || currentUserProfile.role !== 'admin') {
      throw new Error('Admin access required');
    }

    try {
      const activities: Array<{
        id: string;
        type: 'user' | 'subscription' | 'payment' | 'invoice' | 'quote';
        action: string;
        description: string;
        timestamp: string;
        user?: string;
        userId?: string;
      }> = [];

      // Get recent user registrations
      const { data: recentUsers } = await supabaseService.client
        .from('profiles')
        .select('id, full_name, email, created_at')
        .order('created_at', { ascending: false })
        .limit(3);

      recentUsers?.forEach((user: any) => {
        activities.push({
          id: `user-${user.id}`,
          type: 'user',
          action: 'New User',
          description: `${user.full_name || user.email} joined the platform`,
          timestamp: user.created_at,
          user: user.full_name || user.email,
          userId: user.id
        });
      });

      // Get recent subscriptions
      const { data: recentSubs } = await supabaseService.client
        .from('paypal_billing_agreements')
        .select(`
          id,
          status,
          description,
          activated_at,
          created_at,
          profiles:user_id (
            full_name,
            email
          )
        `)
        .in('status', ['active', 'created'])
        .order('created_at', { ascending: false })
        .limit(3);

      recentSubs?.forEach((sub: any) => {
        const action = sub.status === 'active' ? 'Subscription Activated' : 'Subscription Created';
        activities.push({
          id: `sub-${sub.id}`,
          type: 'subscription',
          action,
          description: sub.description || 'New subscription',
          timestamp: sub.activated_at || sub.created_at,
          user: sub.profiles?.full_name || sub.profiles?.email,
          userId: sub.profiles?.id
        });
      });

      // Get recent invoices
      const { data: recentInvoices } = await supabaseService.client
        .from('invoices')
        .select(`
          id,
          invoice_number,
          total_amount,
          created_at,
          profiles:user_id (
            full_name,
            email
          )
        `)
        .order('created_at', { ascending: false })
        .limit(3);

      recentInvoices?.forEach((inv: any) => {
        activities.push({
          id: `invoice-${inv.id}`,
          type: 'invoice',
          action: 'Invoice Created',
          description: `${inv.invoice_number} - $${parseFloat(inv.total_amount || 0).toFixed(2)}`,
          timestamp: inv.created_at,
          user: inv.profiles?.full_name || inv.profiles?.email,
          userId: inv.profiles?.id
        });
      });

      // Get recent payments
      const { data: recentPayments } = await supabaseService.client
        .from('payments')
        .select(`
          id,
          amount,
          currency,
          status,
          payment_date,
          created_at,
          profiles:user_id (
            full_name,
            email
          )
        `)
        .order('created_at', { ascending: false })
        .limit(3);

      recentPayments?.forEach((payment: any) => {
        activities.push({
          id: `payment-${payment.id}`,
          type: 'payment',
          action: 'Payment Received',
          description: `${payment.currency || '$'}${parseFloat(payment.amount || 0).toFixed(2)} - ${payment.status}`,
          timestamp: payment.payment_date || payment.created_at,
          user: payment.profiles?.full_name || payment.profiles?.email,
          userId: payment.profiles?.id
        });
      });

      // Get recent quotes
      const { data: recentQuotes } = await supabaseService.client
        .from('quotes')
        .select(`
          id,
          quote_number,
          total_amount,
          status,
          created_at,
          profiles:user_id (
            full_name,
            email
          )
        `)
        .order('created_at', { ascending: false })
        .limit(2);

      recentQuotes?.forEach((quote: any) => {
        activities.push({
          id: `quote-${quote.id}`,
          type: 'quote',
          action: 'Quote Created',
          description: `${quote.quote_number} - ${quote.status}`,
          timestamp: quote.created_at,
          user: quote.profiles?.full_name || quote.profiles?.email,
          userId: quote.profiles?.id
        });
      });

      // Sort all activities by timestamp descending and limit
      return activities
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, limit);
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      return [];
    }
  }
};

export default adminServiceAPI;